import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { useRequiredContext } from 'hooks';
import type { VariantId } from 'models/product';

interface GlobalPopoversContextType {
	closeContactForm: () => void;
	closeCustomerChat: () => void;
	closeStoreSelector: () => void;
	isContactFormOpen: boolean;
	isCustomerChatOpen: boolean;
	isStoreSelectorOpen: boolean;
	openContactForm: () => void;
	openCustomerChat: () => void;
	openStoreSelector: (variantId: VariantId) => void;
	storeSelectorProductId: VariantId | undefined;
}
const GlobalPopoversContext = createContext<
	GlobalPopoversContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
}

export function GlobalPopoversContextProvider({ children }: Props) {
	const [isStoreSelectorOpen, setIsStoreSelectorOpen] = useState(false);
	const [storeSelectorProductId, setStoreSelectorProductId] = useState<
		VariantId | undefined
	>();

	const [isContactFormOpen, setIsContactFormOpen] = useState(false);
	const [isCustomerChatOpen, setIsCustomerChatOpen] = useState(false);

	const openContactForm = useCallback(() => {
		setIsContactFormOpen(true);
	}, []);
	const closeContactForm = useCallback(() => {
		setIsContactFormOpen(false);
	}, []);
	const openCustomerChat = useCallback(() => {
		setIsCustomerChatOpen(true);
	}, []);
	const closeCustomerChat = useCallback(() => {
		setIsCustomerChatOpen(false);
	}, []);

	const openStoreSelector = useCallback((variantId: VariantId) => {
		setIsStoreSelectorOpen(true);
		setStoreSelectorProductId(variantId);
	}, []);
	const closeStoreSelector = useCallback(() => {
		setIsStoreSelectorOpen(false);
		setStoreSelectorProductId(undefined);
	}, []);
	const value = useMemo(
		() => ({
			openStoreSelector,
			closeStoreSelector,
			isStoreSelectorOpen,
			storeSelectorProductId,
			openContactForm,
			openCustomerChat,
			closeContactForm,
			closeCustomerChat,
			isContactFormOpen,
			isCustomerChatOpen,
		}),
		[
			openStoreSelector,
			closeStoreSelector,
			isStoreSelectorOpen,
			storeSelectorProductId,
			openContactForm,
			openCustomerChat,
			closeContactForm,
			closeCustomerChat,
			isContactFormOpen,
			isCustomerChatOpen,
		],
	);
	return (
		<GlobalPopoversContext value={value}>{children}</GlobalPopoversContext>
	);
}
GlobalPopoversContextProvider.displayName = 'GlobalPopoversContextProvider';

export function useGlobalPopoversContext() {
	return useRequiredContext(GlobalPopoversContext, 'GlobalPopoversContext');
}
